'use client';

import { motion } from 'framer-motion';
import type { Property } from '@/lib/api/clients/properties';
import { MarkdownRenderer } from '@routeroyal/ui-core';

export interface StoneInvestmentPropertyOverviewProps {
  property: Property;
  locale?: string;
  messages?: Record<string, unknown>;
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Stone Investment Property Overview
 * 
 * Property details section with:
 * - Description
 * - Key features grid
 * - Specifications table
 * - Elegant typography
 */
export function StoneInvestmentPropertyOverview({
  property,
  locale = 'en',
  messages,
}: StoneInvestmentPropertyOverviewProps) {
  const propertyMsg = messages?.property as Record<string, unknown> | undefined;

  const area = property.totalFloorArea 
    ? `${property.totalFloorArea} ${property.totalFloorAreaUnit === 'SQM' ? 'm²' : 'sqft'}`
    : null;

  const landArea = property.landArea 
    ? `${property.landArea} ${property.landAreaUnit === 'SQM' ? 'm²' : property.landAreaUnit || 'sqft'}`
    : null;

  // Build specifications list
  const specifications = [
    { label: safeGetString(propertyMsg?.bedrooms, 'Bedrooms'), value: property.bedrooms },
    { label: safeGetString(propertyMsg?.bathrooms, 'Bathrooms'), value: property.bathrooms },
    { label: safeGetString(propertyMsg?.floorArea, 'Floor Area'), value: area },
    { label: safeGetString(propertyMsg?.landArea, 'Land Area'), value: landArea },
    { label: safeGetString(propertyMsg?.yearBuilt, 'Year Built'), value: property.yearBuilt },
    { label: safeGetString(propertyMsg?.parking, 'Parking Spaces'), value: property.parkingSpaces },
    { label: safeGetString(propertyMsg?.storeys, 'Storeys'), value: property.storeys },
    { label: safeGetString(propertyMsg?.condition, 'Condition'), value: property.condition },
  ].filter(spec => spec.value !== null && spec.value !== undefined);

  return (
    <section className="py-20 lg:py-28 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Description Column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-px bg-[var(--color-text)]" />
              <span 
                className="text-xs tracking-[0.3em] uppercase text-[var(--color-text-muted)]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {safeGetString(propertyMsg?.overview, 'Overview')}
              </span>
            </div>

            <h2 
              className="text-3xl lg:text-4xl text-[var(--color-text)] tracking-[-0.02em] mb-8"
              style={{ 
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                fontWeight: 300,
              }}
            >
              {safeGetString(propertyMsg?.aboutProperty, 'About This Property')}
            </h2>

            {/* Description */}
            <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <MarkdownRenderer
                content={property.description || 
                  `This exceptional property represents the finest in ${locale === 'fr' ? 'immobilier de prestige' : 'prestige real estate'}. Contact us for more details about this unique opportunity.`
                }
                theme="stone"
              />
            </div>

            {/* Reference Number */}
            {property.reference && (
              <div className="mt-10 pt-6 border-t border-[var(--color-border)]">
                <p 
                  className="text-xs text-[var(--color-text-muted)] tracking-wider"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <span className="text-[var(--color-text-muted)]">Reference:</span>{' '}
                  <span className="text-[var(--color-text)]">{property.reference}</span>
                </p>
              </div>
            )}
          </motion.div>

          {/* Specifications Column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 lg:p-10">
              <h3 
                className="text-xl text-[var(--color-text)] mb-8 pb-4 border-b border-[var(--color-border)]"
                style={{ 
                  fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                  fontWeight: 400,
                }}
              >
                {safeGetString(propertyMsg?.specifications, 'Key Details')}
              </h3>

              <div className="space-y-4">
                {specifications.map((spec, index) => (
                  <div 
                    key={index} 
                    className="flex justify-between items-center py-3 border-b border-[var(--color-border)]/50 last:border-b-0"
                  >
                    <span 
                      className="text-sm text-[var(--color-text-muted)]"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {spec.label}
                    </span>
                    <span 
                      className="text-sm text-[var(--color-text)] font-medium"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {spec.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Enquiry CTA */}
              <div className="mt-10 pt-6 border-t border-[var(--color-border)]">
                <a
                  href="#contact"
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-[var(--color-text)] text-[var(--color-background)] text-xs tracking-[0.15em] uppercase transition-all duration-300 hover:bg-[var(--color-text)]/90"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <span>{safeGetString(propertyMsg?.enquire, 'Enquire About This Property')}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
